import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtDecodeService } from './jwt-decode.service';

type User = {
  siteAdm: boolean,
  MCADD: boolean,
  MCEdit: boolean,
  MCDelete: boolean,
  CCAdd: boolean,
  CCDelete: boolean,
  webAdm: boolean,
  name: string;
  username: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user = signal<User | null>(null);
  userDetail: any;
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private userLoginStatus = new BehaviorSubject<string | null>(null);
  private userLogoutMessageEmit = new BehaviorSubject<any>('');
  logoutMessage = {
    type1: 'For your security we automatically terminate your session after a period of inactivity. This feature helps prevent unauthorized users from accessing your information if you leave your computer unattended for an extended period. Please log in again.',
    type2: 'You have been successfully logged out!',
    type3: 'Something went wrong! Please try again!',
    type4: 'Invalid Username or Password!'
  };

  // Public observable for subscribers
  userLoggedInMessage$: Observable<string | null> = this.userLoginStatus.asObservable();
  userLogoutMessage$: Observable<string> = this.userLogoutMessageEmit.asObservable();

  router = inject(Router);
  apiService = inject(ApiService);
  jwtDecodeService = inject(JwtDecodeService);

  get siteAdm(): boolean {
    return this.user()?.siteAdm ? true : false;
  }

  get MCAdd(): boolean {
    return this.user()?.MCADD ? true : false;
  }

  get MCEdit(): boolean {
    return this.user()?.MCEdit ? true : false;
  }

  get MCDelete(): boolean {
    return this.user()?.MCDelete ? true : false;
  }

  get CCAdd(): boolean {
    return this.user()?.CCAdd ? true : false;
  }

  get CCDelete(): boolean {
    return this.user()?.CCDelete ? true : false;
  }

  get webAdm(): boolean {
    return this.user()?.webAdm ? true : false;
  }

  get username(): string {
    return this.user()?.username || '';
  }

  get name(): string {
    return this.user()?.name || '';
  }

  isAuth(): boolean {
    return !!this.user();
  }

  isAuthGuard(): boolean {
    const isAuth = this.isAuth();
    if (isAuth) return true;
    this.router.navigate(['/login']);
    return false;
  }

  setAccessToken(accessToken: string) {
    this.accessToken$.next(accessToken);
  }

  getAccessToken() {
    return this.accessToken$.value;
  }

  setLogoutMessage(msg: string, messagetype: string) {
    let messageData: any = {
      "message": msg,
      "type": messagetype
    };
    this.userLogoutMessageEmit.next(messageData);
  }

  getLogoutMessage() {
    return this.userLogoutMessageEmit.value;
  }

  logout(messagetype: 'type1' | 'type2' | 'type3' | 'type4', callLogoutAPI = true): void {
    if (callLogoutAPI) {
      this.apiService.logoutAPI().subscribe(res => {
      });
    }
    this.userLoginStatus.next('loggedOut');
    this.setLogoutMessage(this.logoutMessage[messagetype], messagetype);
    this.user.set(null);
    this.router.navigate(['/login']);
  }

  setUserData() {
    this.userLoginStatus.next('loggedIn');
    this.user.set({
      siteAdm: this.userDetail && this.userDetail.roles.includes('Site Adm') ? true : false,
      MCADD: this.userDetail && this.userDetail.roles.includes('MCAdd') ? true : false,
      MCEdit: this.userDetail && this.userDetail.roles.includes('MCEdit') ? true : false,
      MCDelete: this.userDetail && this.userDetail.roles.includes('MCDelete') ? true : false,
      CCAdd: this.userDetail && this.userDetail.roles.includes('CCAdd') ? true : false,
      CCDelete: this.userDetail && this.userDetail.roles.includes('CCDelete') ? true : false,
      webAdm: this.userDetail && this.userDetail.roles.includes('Web Adm') ? true : false,
      name: this.userDetail ? this.userDetail.fname + " " + this.userDetail.lname : '',
      username: this.userDetail ? this.userDetail.sub : ''
    });
  }

  initLoggedInUser(res: any) {
    this.setAccessToken(res.accessToken);
    this.userDetail = this.jwtDecodeService.decodeToken(res.accessToken);
    this.setUserData();
  }
}
